import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import type { Store } from '@reduxjs/toolkit';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';
import { Button, FlashMessage, TextField } from 'c-components';
import { isApiErrorMessage, NetworkRequestState } from 'c-data-layer';
import apiClient from 'c-data/apiClient';
import { useIsMobileView } from 'c-hooks';
import { Translate, useCommonTranslation } from 'c-translation';
import type { CommonState } from 'c-types';
import { authSelectors, authThunks } from '../../Slices';
import retryAfterRelogin from '../../Lib/retryAfterRelogin';

const titleId = 'session-expired-dialog-title';
const dialogSx = { zIndex: theme => theme.zIndex.modal + 1 };

const isAuthRequest = (url: string | undefined) =>
    url?.startsWith(`${apiClient.HttpClient.BaseUrl()}auth/`) ?? false;

/**
 * Opens over the current page when the API answers 401 to a logged-in user, and asks for the
 * password again. The page underneath stays mounted with its input, and the requests that got the
 * 401 are sent again once the user is back in (`retryAfterRelogin`). Rendered once, in `AppWrapper`.
 */
const SessionExpiredDialog = () => {
    const store = useStore() as Store<CommonState>;
    const dispatch = useDispatch();
    const isMobile = useIsMobileView();
    const user = useSelector(authSelectors.authenticatedUser);
    const expired = useSelector(authSelectors.sessionTimedOut);
    const reloginState = useSelector(authSelectors.reloginState);
    const [password, setPassword] = useState('');

    useEffect(() => retryAfterRelogin(apiClient.HttpClient.Axios(), store, isAuthRequest), [store]);

    const open = expired && user != null;
    const failed = reloginState.state === NetworkRequestState.Error;
    const inProgress = reloginState.state === NetworkRequestState.InProgress;

    useEffect(() => {
        if (failed || !open) setPassword('');
    }, [failed, open]);

    const onSubmit = useCallback(
        (e: React.FormEvent) => {
            e.preventDefault();
            dispatch(authThunks.relogin({ password, isMobile }));
        },
        [dispatch, password, isMobile],
    );
    const onLogout = useCallback(() => dispatch(authThunks.logoutLoggedInUser()), [dispatch]);

    const t = useCommonTranslation();

    return (
        <Dialog
            open={open}
            aria-labelledby={titleId}
            disableEscapeKeyDown
            maxWidth="xs"
            fullWidth
            sx={dialogSx}
        >
            <form onSubmit={onSubmit}>
                <DialogTitle id={titleId}>
                    <Translate path="Modules.Auth.SessionExpiredDialog.title" />
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Typography variant="body2">
                            <Translate path="Modules.Auth.SessionExpiredDialog.description" />
                        </Typography>
                        {failed && (
                            <FlashMessage status="error">
                                {isApiErrorMessage(reloginState.error)
                                    ? reloginState.error
                                    : t('Pages.Login.genericLoginError')}
                            </FlashMessage>
                        )}
                        <TextField
                            label={t('Pages.Login.emailInputLabel')}
                            value={user?.email ?? ''}
                            disabled
                        />
                        <TextField
                            label={t('Pages.Login.passwordInputLabel')}
                            type="password"
                            autoComplete="current-password"
                            autoFocus
                            required
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant="text" onClick={onLogout}>
                        <Translate path="Navigation.logoutLabel" />
                    </Button>
                    <Button type="submit" disabled={inProgress || password.length === 0}>
                        <Translate path="Pages.Login.signInButtonLabel" />
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default SessionExpiredDialog;
